import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import DialogTransition from 'src/components/DialogTransition';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'src/utils/dayjs';
import { toMexicoCityInstant } from 'src/utils/momentLocalTimezone';
import useStyles from './styles';
import validationSchema from './schema';

const EditAutomaticDebitCollectionDueDateForm = ({
  open,
  onClose,
  automaticDebitCollectionId,
  onSubmit,
  currentDueDate
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const resetRef = useRef();

  const { minDate, initialDate } = useMemo(() => {
    const today = toMexicoCityInstant().startOf('day');
    const dueDateMoment = currentDueDate
      ? toMexicoCityInstant(currentDueDate)
      : today;

    // Get the first day of the due date's month
    const firstDayOfMonth = dueDateMoment.startOf('month');

    // If due date's month is current month, use today as min date
    // Otherwise use first day of the due date's month
    const minDateValue = dueDateMoment.isSame(today, 'month')
      ? today
      : firstDayOfMonth;

    return {
      minDate: minDateValue,
      initialDate: dueDateMoment
    };
  }, [currentDueDate]);

  if (!open) return null;

  const handleFormSubmit = values => {
    onSubmit({
      id: automaticDebitCollectionId,
      dueDate: dayjs(values.dueDate).toISOString()
    });
  };

  return (
    <Dialog
      disableEscapeKeyDown
      classes={{ paper: classes.dialogRoot }}
      open={open}
      keepMounted
      TransitionComponent={DialogTransition}
    >
      <DialogTitle className={classes.header}>
        <IconButton
          key="close"
          aria-label="Close"
          color="inherit"
          onClick={onClose}
          className={classes.closeIcon}
        >
          <CloseIcon />
        </IconButton>
        {t('ENUMS.AUTOMATIC_DEBIT.SET_NEW_DUE_DATE_TITLE')}
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={{ dueDate: initialDate }}
          validationSchema={validationSchema(currentDueDate)}
          onSubmit={handleFormSubmit}
        >
          {({
            errors,
            handleSubmit,
            touched,
            values,
            resetForm,
            setFieldValue
          }) => {
            resetRef.current = () => resetForm();
            return (
              <Form onSubmit={handleSubmit}>
                <FormControl
                  fullWidth
                  error={Boolean(touched.dueDate && errors.dueDate)}
                >
                  <DatePicker
                    label={t('ENUMS.AUTOMATIC_DEBIT.DUE_DATE')}
                    format="DD/MM/YYYY"
                    value={values.dueDate}
                    onChange={newValue => {
                      setFieldValue('dueDate', newValue);
                    }}
                    minDate={minDate}
                    views={['year', 'month', 'day']}
                    openTo="day"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: Boolean(touched.dueDate && errors.dueDate),
                        helperText: touched.dueDate && t(errors.dueDate),
                        sx: { marginTop: 2, marginBottom: 2 }
                      }
                    }}
                  />
                </FormControl>
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    margin: '1rem auto'
                  }}
                >
                  <Button
                    onClick={onClose}
                    color="primary"
                    variant="outlined"
                    sx={{ minWidth: '45%' }}
                  >
                    {t('COMMON.CANCEL')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ minWidth: '45%' }}
                  >
                    {t('COMMON.ACCEPT')}
                  </Button>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

EditAutomaticDebitCollectionDueDateForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  automaticDebitCollectionId: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  currentDueDate: PropTypes.string
};

export default EditAutomaticDebitCollectionDueDateForm;
